import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { DateRangeType, DateValueType } from "react-tailwindcss-datepicker";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);

export const formatDate = (dateStr: string | Date, format = "MMMM D, YYYY") => {
  return dayjs(dateStr).local().format(format);
};

/** e.g. "2 hours ago", "yesterday" */
export const getRelativeTime = (dateStr: string | Date): string => {
  return dayjs(dateStr).fromNow();
};

/** e.g. "Tuesday, July 15" — parses "YYYY-MM-DD" strings as local dates */
export const formatFullDate = (date: string | Date = new Date()): string =>
  dayjs(date).format("dddd, MMMM D");

/** "today" / "yesterday" for a label produced by formatFullDate, null otherwise */
export const relativeDayFromLabel = (label: string): "today" | "yesterday" | null => {
  if (label === formatFullDate()) return "today";
  if (label === formatFullDate(dayjs().subtract(1, "day").toDate())) return "yesterday";
  return null;
};

/** "Good morning" / "Good afternoon" / "Good evening" based on the local time */
export const timeGreeting = (): string => {
  const h = dayjs().hour();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
};

export const strToDate = (str: string): Date => dayjs(str).toDate();

export const getPreviousYear = (str?: string, format = "YYYY-MM-DD") => {
  const baseDate = dayjs(str);
  const previousYear = baseDate.subtract(1, "year");
  const response = previousYear.format(format);
  return response;
};

/**
 *
 * @param format string
 * @description default range is last 30 days
 */
export const getDefaultRange = (format = "YYYY-MM-DD"): DateRangeType => {
  const baseDate = dayjs();
  const previousYear = baseDate.subtract(29, "days");

  const startDate = new Date(previousYear.format(format));
  const endDate = new Date(baseDate.format(format));
  return { startDate, endDate };
};

export const getDiffInDays = (endDate: Date | string, startDate: Date | string) =>
  dayjs(endDate).diff(startDate, "days");

export const getTimestamp = () => {
  const date = new Date();
  return Math.floor(date.getTime() / 1000);
};

export const formatDurationToHumanReadable = (timeStr: string): string => {
  const [hh, mm] = timeStr.split(":").map(Number);

  const parts: string[] = [];

  if (hh > 0) {
    parts.push(`${hh}h`);
  }

  if (mm > 0 || hh === 0) {
    parts.push(`${mm}min`);
  }

  return parts.join(" ");
};


export const isValidRange = (dateRange: DateValueType): boolean => {
  return !!dateRange?.startDate && !!dateRange?.endDate;
}

export const getUserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'UTC'; // Fallback
  }
};

export const getMonthOptions = () => Array.from({ length: 12 }, (_, i) => {
  const monthNumber = i + 1;
  const monthName = new Date(2000, i, 1).toLocaleString('default', { month: 'long' });
  return { value: monthNumber, label: monthName };
});

export const getYearOptions = (amount = 5) => Array.from({ length: amount }, (_, i) => {
  const year = new Date().getFullYear() - i;
  return { value: year, label: year.toString() };
});

// Filter months based on selected year
export const getFilteredMonthOptions = (selectedYear: number) => {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const allMonths = getMonthOptions();

  // If selected year is current year, only show months up to current month
  if (selectedYear === currentYear) {
    return allMonths.filter((month) => month.value <= currentMonth);
  }

  // If selected year is in the past, show all months
  return allMonths;
};


/** Converts 12h format to 24h */
export function to24h(hours: number, period: 'AM' | 'PM'): number {
  if (period === 'AM') return hours === 12 ? 0 : hours;
  return hours === 12 ? 12 : hours + 12;
}


/** Build ISO string from date string + time components (local → UTC) */
export function buildLocalISOString(
  dateStr: string,
  hours: number,
  minutes: number,
  period: 'AM' | 'PM'
): string {
  const h24 = to24h(hours, period);
  const localTz = dayjs.tz.guess();
  const local = dayjs.tz(dateStr, localTz).hour(h24).minute(minutes).second(0).millisecond(0);
  return local.utc().toISOString();
}

/** Extract local time parts from an ISO (UTC) string */
export function extractLocalTime(isoString: string): { hours: number; minutes: number; period: 'AM' | 'PM' } {
  const local = dayjs(isoString).local();
  const hour24 = local.hour();
  const period: 'AM' | 'PM' = hour24 >= 12 ? 'PM' : 'AM';
  const hours = hour24 % 12 || 12;
  return { hours, minutes: local.minute(), period };
}